import { visitorCountry } from '@/components/client/site-popups/visitor-country'
import { parseGuideEligibility, type GuideEligibilityReason } from '@/lib/analytics/guide-eligibility'
import type { HubVisit } from '@/lib/analytics/hub-visit'
import { uuidv7 } from '@/lib/analytics/uuid'

interface EligibilityResult {
  reason: GuideEligibilityReason | 'network_error' | 'invalid_response' | 'aborted'
  httpStatus?: number
}

const requestEligibility = async (signal: AbortSignal): Promise<EligibilityResult> => {
  try {
    const response = await fetch('/api/hearing-aids/guide-eligibility', {
      method: 'POST',
      cache: 'no-store',
      signal,
    })
    const result = parseGuideEligibility(await response.json().catch(() => null))
    // A non-2xx response can explain a failure but can never authorize mounting.
    return {
      reason: result && (response.ok || !result.eligible) ? result.reason : 'invalid_response',
      httpStatus: response.status,
    }
  } catch {
    return { reason: signal.aborted ? 'aborted' : 'network_error' }
  }
}

class GuideEligibilityCheck {
  private controller = new AbortController()
  private checkId = uuidv7()
  private active = true
  private startedAt: number | null = null
  private settled = false

  constructor(
    private visit: HubVisit | null,
    private onEligible: () => void
  ) {}

  private finish = ({ reason, httpStatus }: EligibilityResult) => {
    if (this.settled || this.startedAt === null) {
      return
    }
    this.settled = true
    const latencyMs = Math.max(0, Date.now() - this.startedAt)
    this.visit?.updateGuide({ guideEligibility: reason, guideEligibilityMs: latencyMs })
    this.visit?.track('hub_guide_eligibility_resolved', {
      checkId: this.checkId,
      reason,
      eligible: reason === 'verified',
      latencyMs,
      httpStatus,
    })
    if (this.active && reason === 'verified') {
      this.onEligible()
    }
  }

  start = () => {
    if (!this.active) {
      return
    }
    this.startedAt = Date.now()
    const country = visitorCountry()
    this.visit?.updateGuide({ guideEligibility: 'pending' })
    this.visit?.track('hub_guide_eligibility_started', {
      checkId: this.checkId,
      verificationRequested: country === 'US',
    })
    if (country !== 'US') {
      this.finish({ reason: country ? 'country_not_us' : 'country_unknown' })
      return
    }
    void requestEligibility(this.controller.signal).then((result) => {
      if (this.active) {
        this.finish(result)
      }
    })
  }

  stop = () => {
    this.active = false
    this.controller.abort()
    this.finish({ reason: 'aborted' })
  }
}

export const startGuideEligibility = (visit: HubVisit | null, onEligible: () => void) => {
  const check = new GuideEligibilityCheck(visit, onEligible)
  // Wait for the analytics parent's effect; cancel Strict Mode rehearsals.
  queueMicrotask(check.start)
  return check.stop
}
